import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useSpring, useTransform } from "framer-motion";
import "./medical-intake.css";
import { Check, ChevronDown, ChevronUp, X } from "lucide-react";
import { Modal, Button, Text } from "@mantine/core";
import { useNavigate } from "react-router";

const MedicalIntake = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    medicalConditions: "",
    familyMedicalConditions: "",
    medications: "",
    allergies: "",
    surgeries: "",
    doctorName: "",
    hospitalName: "",
    doctorEmail: "",
    medicalConditionsSpecify: "",
    familyMedicalConditionsSpecify: "",
    medicationsSpecify: "",
    allergiesSpecify: "",
    surgeriesSpecify: "",
  });

  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("initial");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isHealthcareOpen, setIsHealthcareOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [modalOpened, setModalOpened] = useState(false);
  const uploadInterval = React.useRef(null);

  const API_ENDPOINT = "/wp-json/dbsb/v1/upload-medical-intake";

  // Smooth progress animation using useSpring
  const springProgress = useSpring(0, { stiffness: 100, damping: 20 });
  const displayProgress = useTransform(springProgress, (value) => `${Math.round(value)}%`);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validFormats = ["application/pdf", "image/jpeg", "image/png"];
      const maxSize = 10 * 1024 * 1024;

      if (!validFormats.includes(selectedFile.type)) {
        setError("File format not supported");
        setUploadStatus("failed");
        setFile(selectedFile);
        return;
      }

      if (selectedFile.size > maxSize) {
        setError("File size exceeds 10MB limit");
        setUploadStatus("failed");
        setFile(selectedFile);
        return;
      }

      setFile(selectedFile);
      setUploadStatus("uploading");
      setError("");
      setSuccess("");
      setIsUploading(true);

      let currentProgress = 0;

      // Animate progress smoothly up to 90%
      const animateProgress = () => {
        if (currentProgress < 90) {
          currentProgress = Math.min(currentProgress + 2, 90);
          setProgress(currentProgress);
          springProgress.set(currentProgress);
          uploadInterval.current = setTimeout(animateProgress, 100);
        }
      };

      animateProgress();

      // Stop at 90% since the actual upload happens on form submission
      setUploadStatus("complete");
      setProgress(90);
      springProgress.set(90);
      setIsUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const fileEvent = { target: { files: [droppedFile] } };
      handleFileChange(fileEvent);
    }
  };

  const handleUploadAnother = () => {
    setFile(null);
    setUploadStatus("initial");
    setError("");
    setSuccess("");
    setProgress(0);
    setIsUploading(false);
    if (uploadInterval.current) {
      clearTimeout(uploadInterval.current);
      uploadInterval.current = null;
    }
  };

  const handleCancelUpload = () => {
    if (uploadInterval.current) {
      clearTimeout(uploadInterval.current);
      uploadInterval.current = null;
    }

    setUploadStatus("initial");
    setFile(null);
    setProgress(0);
    springProgress.set(0);
    setError("");
    setSuccess("");
    setIsUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isFormIncomplete()) return;

    setIsLoading(true);
    setApiError(null);

    try {
      if (!window.wpApiSettings?.nonce) {
        throw new Error("Authentication token not found. Please try logging in again.");
      }

      const submissionData = new FormData();

      // Prepare JSON data (excluding empty optional fields)
      const jsonData = {
        medical_conditions: formData.medicalConditions,
        medications: formData.medications,
        allergies: formData.allergies,
        surgeries: formData.surgeries,
      };

      if (formData.medicalConditions === "Yes") {
        jsonData.medical_conditions_specify = formData.medicalConditionsSpecify || "";
        jsonData.family_medical_conditions = formData.familyMedicalConditions;
        if (formData.familyMedicalConditions === "Yes") {
          jsonData.family_medical_conditions_specify = formData.familyMedicalConditionsSpecify || "";
        }
      }

      if (formData.medications === "Yes") {
        jsonData.medications_specify = formData.medicationsSpecify || "";
      }

      if (formData.allergies === "Yes") {
        jsonData.allergies_specify = formData.allergiesSpecify || "";
      }

      if (formData.surgeries === "Yes") {
        jsonData.surgeries_specify = formData.surgeriesSpecify || "";
      }

      if (formData.doctorName) {
        jsonData.doctor_name = formData.doctorName;
      }

      if (formData.hospitalName) {
        jsonData.hospital_name = formData.hospitalName;
      }

      if (formData.doctorEmail) {
        jsonData.doctor_email = formData.doctorEmail;
      }

      // Add JSON data to FormData
      submissionData.append("form_data", JSON.stringify(jsonData));

      // Add file if it exists and upload status is complete
      if (file && uploadStatus === "complete") {
        submissionData.append("medical_intake", file);
      }

      // Debug log to verify FormData content
      console.log("FormData content:");
      for (let [key, value] of submissionData.entries()) {
        console.log(`${key}: ${value instanceof File ? value.name : value}`);
      }

      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        body: submissionData,
        credentials: "include",
        headers: {
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        switch (response.status) {
          case 401:
            throw new Error("You must be logged in to submit the form. Please log in and try again.");
          case 403:
            throw new Error("You don't have permission to submit this form.");
          default:
            throw new Error(data.message || "Failed to submit medical intake form. Please try again.");
        }
      }

      setModalOpened(true);
      setFormData({
        medicalConditions: "",
        familyMedicalConditions: "",
        medications: "",
        allergies: "",
        surgeries: "",
        doctorName: "",
        hospitalName: "",
        doctorEmail: "",
        medicalConditionsSpecify: "",
        familyMedicalConditionsSpecify: "",
        medicationsSpecify: "",
        allergiesSpecify: "",
        surgeriesSpecify: "",
      });
      setFile(null);
      setUploadStatus("initial");
      setProgress(0);
      setError("");
      setSuccess("");
      setIsHealthcareOpen(false);
      setIsUploadOpen(false);
    } catch (error) {
      setApiError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const isFormIncomplete = () => {
    const requiredFields = {
      medicalConditions: formData.medicalConditions,
      medications: formData.medications,
      allergies: formData.allergies,
      surgeries: formData.surgeries,
    };

    if (formData.medicalConditions === "Yes") {
      requiredFields.familyMedicalConditions = formData.familyMedicalConditions;
    }

    return Object.values(requiredFields).some((value) => value === "");
  };

  useEffect(() => {
    // This ensures the button state updates whenever formData changes
  }, [formData]);

  const formVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.02,
      backgroundColor: "#d1d9e6",
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.98 },
  };

  const uploadAreaVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" } },
    uploading: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: "easeInOut" } },
    complete: {
      opacity: 1,
      scale: 1.02,
      backgroundImage:
        "linear-gradient(260.26deg, rgba(47, 64, 211, 0.07) 12.78%, rgba(214, 215, 225, 0.07) 118.25%)",
      transition: { duration: 0.4, ease: "easeOut" },
    },
    failed: { opacity: 1, scale: 0.98, transition: { duration: 0.4, ease: "easeIn" } },
  };

  const sectionVariants = {
    hidden: { height: 0, opacity: 0, y: -20, overflow: "hidden" },
    visible: { height: "auto", opacity: 1, y: 0, overflow: "visible", transition: { duration: 0.3, ease: "easeInOut" } },
    exit: { height: 0, opacity: 0, y: -20, overflow: "hidden", transition: { duration: 0.3, ease: "easeInOut" } },
  };

  return (
    <div className="medical-intake-container">
      <motion.div
        className="medical-intake-content"
        variants={formVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h2 variants={itemVariants}>Fill medical intake form</motion.h2>
        <motion.div className="medical-intake-card" variants={itemVariants}>
          <motion.form
            onSubmit={handleSubmit}
            className="medical-intake-form"
            variants={formVariants}
          >
            {/* Medical History Section */}
            <motion.div variants={itemVariants} className="form-group">
              <label>Medical history</label>
              <div className="sub-group">
                <label>Do you have any existing medical conditions?</label>
                <select
                  name="medicalConditions"
                  value={formData.medicalConditions}
                  onChange={handleChange}
                >
                  <option value="">Select from dropdown</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              {formData.medicalConditions === "Yes" && (
                <>
                  <div className="sub-group">
                    <textarea
                      name="medicalConditionsSpecify"
                      placeholder="If Yes, specify conditions"
                      rows="4"
                      value={formData.medicalConditionsSpecify}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="sub-group">
                    <label>
                      Does any one in your family (siblings, parents, uncles, aunts, grandparents) have any of these conditions?
                    </label>
                    <select
                      name="familyMedicalConditions"
                      value={formData.familyMedicalConditions}
                      onChange={handleChange}
                    >
                      <option value="">Select from dropdown</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                  {formData.familyMedicalConditions === "Yes" && (
                    <div className="sub-group">
                      <textarea
                        name="familyMedicalConditionsSpecify"
                        placeholder="If Yes, specify conditions"
                        rows="4"
                        value={formData.familyMedicalConditionsSpecify}
                        onChange={handleChange}
                      />
                    </div>
                  )}
                </>
              )}
              <div className="sub-group">
                <label>Are you currently taking any medications?</label>
                <select
                  name="medications"
                  value={formData.medications}
                  onChange={handleChange}
                >
                  <option value="">Select from dropdown</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              {formData.medicalConditions === "Yes" && (
                <div className="sub-group">
                  <textarea
                    name="medicationsSpecify"
                    placeholder="If Yes, specify medications"
                    rows="4"
                    value={formData.medicationsSpecify}
                    onChange={handleChange}
                  />
                </div>
              )}
              <div className="sub-group">
                <label>Do you have any allergies?</label>
                <select
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleChange}
                >
                  <option value="">Select from dropdown</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              {formData.allergies === "Yes" && (
                <div className="sub-group">
                  <textarea
                    name="allergiesSpecify"
                    placeholder="If Yes, specify allergies"
                    rows="4"
                    value={formData.allergiesSpecify}
                    onChange={handleChange}
                  />
                </div>
              )}
              <div className="sub-group">
                <label>Have you undergone any major surgeries?</label>
                <select
                  name="surgeries"
                  value={formData.surgeries}
                  onChange={handleChange}
                >
                  <option value="">Select from dropdown</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              {formData.surgeries === "Yes" && (
                <div className="sub-group">
                  <textarea
                    name="surgeriesSpecify"
                    placeholder="If Yes, specify surgeries"
                    rows="4"
                    value={formData.surgeriesSpecify}
                    onChange={handleChange}
                  />
                </div>
              )}
            </motion.div>

            {/* Healthcare Provider Information Section (Collapsible) */}
            <motion.div variants={itemVariants} className="form-group">
              <div
                className="collapsible-header"
                onClick={() => setIsHealthcareOpen(!isHealthcareOpen)}
              >
                <label>Healthcare provider information (optional)</label>
                {isHealthcareOpen ? (
                  <ChevronUp className="toggle-icon" />
                ) : (
                  <ChevronDown className="toggle-icon" />
                )}
              </div>
              <AnimatePresence>
                {isHealthcareOpen && (
                  <motion.div
                    variants={sectionVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <div className="sub-group">
                      <label>Doctor's name</label>
                      <input
                        name="doctorName"
                        value={formData.doctorName}
                        onChange={handleChange}
                        placeholder="Enter name here"
                      />
                    </div>
                    <div className="sub-group">
                      <label>Hospital's name</label>
                      <input
                        name="hospitalName"
                        value={formData.hospitalName}
                        onChange={handleChange}
                        placeholder="Enter hospital here"
                      />
                    </div>
                    <div className="sub-group">
                      <label>Doctor's email</label>
                      <input
                        type="email"
                        name="doctorEmail"
                        value={formData.doctorEmail}
                        onChange={handleChange}
                        placeholder="Enter email here"
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Upload Prescription Section (Collapsible) */}
            <motion.div variants={itemVariants} className="form-group">
              <div
                className="collapsible-header"
                onClick={() => setIsUploadOpen(!isUploadOpen)}
              >
                <label>Upload prescription (optional)</label>
                {isUploadOpen ? (
                  <ChevronUp className="toggle-icon" />
                ) : (
                  <ChevronDown className="toggle-icon" />
                )}
              </div>
              <AnimatePresence>
                {isUploadOpen && (
                  <motion.div
                    variants={sectionVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <motion.div
                      key={uploadStatus}
                      className={`upload-area ${
                        uploadStatus === "complete" ? "upload-complete" : ""
                      }`}
                      variants={uploadAreaVariants}
                      initial="hidden"
                      animate={
                        uploadStatus === "uploading"
                          ? "uploading"
                          : uploadStatus === "complete"
                          ? "complete"
                          : uploadStatus === "failed"
                          ? "failed"
                          : "visible"
                      }
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    >
                      {uploadStatus === "initial" && (
                        <>
                          <img
                            src={`${process.env.PUBLIC_URL}/assets/folder-open.svg`}
                            className="file-icon"
                            alt=""
                          />
                          <p>Drag & drop your file here</p>
                          <div className="or-line">
                            <ul></ul>
                            <p>OR</p>
                            <ul></ul>
                          </div>
                          <label className="browse-button">
                            Browse files
                            <input
                              type="file"
                              onChange={handleFileChange}
                              accept=".pdf,.jpg,.png"
                              style={{ display: "none" }}
                              disabled={isUploading}
                            />
                          </label>
                        </>
                      )}
                      {uploadStatus === "uploading" && (
                        <>
                          <motion.div
                            className="progress-bar"
                            style={{ width: displayProgress }}
                          />
                          <img
                            src={`${process.env.PUBLIC_URL}/assets/folder-close.svg`}
                            className="file-icon"
                            alt=""
                          />
                          <p>{file?.name}</p>
                          <div className="upload-status">
                            <span>{Math.round(progress)}%</span>
                            <span>Uploading</span>
                            <span className="dot"></span>
                            <span className="file-size">
                              {((file?.size * progress) / 100 / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                          <button
                            className="cancel-button"
                            onClick={handleCancelUpload}
                            disabled={!isUploading}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                      {uploadStatus === "complete" && (
                        <>
                          <img
                            src={`${process.env.PUBLIC_URL}/assets/folder-close.svg`}
                            className="file-icon"
                            alt=""
                          />
                          <p>{file?.name}</p>
                          <p className="upload-status-message-success">
                            <Check size={16} className="status-icon" /> Upload complete
                          </p>
                          <button
                            className="upload-another-button"
                            onClick={handleUploadAnother}
                          >
                            Upload another file
                          </button>
                        </>
                      )}
                      {uploadStatus === "failed" && (
                        <>
                          <img
                            src={`${process.env.PUBLIC_URL}/assets/folder-close.svg`}
                            className="file-icon"
                            alt=""
                          />
                          <p>{file?.name}</p>
                          <p className="upload-status-message-failed">
                            <X size={16} className="status-icon" /> {error}
                          </p>
                          <button
                            className="upload-another-button"
                            onClick={handleUploadAnother}
                          >
                            Upload another file
                          </button>
                        </>
                      )}
                    </motion.div>
                    {error && uploadStatus !== "failed" && (
                      <span className="error">{error}</span>
                    )}
                    {success && <span className="success-message">{success}</span>}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.form>
        </motion.div>
        {apiError && (
          <motion.p className="error-message" variants={itemVariants}>
            {apiError}
          </motion.p>
        )}
        <motion.div variants={itemVariants} className="button-group">
          <motion.button
            type="submit"
            onClick={handleSubmit}
            className={`submit-button ${isFormIncomplete() || isLoading ? "disabled" : ""}`}
            disabled={isFormIncomplete() || isLoading}
            variants={buttonVariants}
            whileHover={isFormIncomplete() || isLoading ? {} : "hover"}
            whileTap={isFormIncomplete() || isLoading ? {} : "tap"}
          >
            {isLoading ? "Submitting..." : "Submit"}
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Success Modal */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        withCloseButton={false}
        centered
        size="sm"
        className="success-modal"
        overlayProps={{
          opacity: 0.55,
          blur: 3,
        }}
      >
        <div className="modal-content">
          <img
            src={`${process.env.PUBLIC_URL}/assets/logo.svg`}
            alt="Direct by Syndicate Bio"
            className="modal-logo"
          />
          <div className="modal-message-box">
            <Text className="modal-title">Success!</Text>
            <Text className="modal-message">
              Medical intake form submitted successfully{file && uploadStatus === "complete" ? " with your uploaded file." : "."}
            </Text>
            <Button
              onClick={() => navigate("/dashboard")}
              className="modal-close-button"
              style={{height: "fit-content",}}
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MedicalIntake;